import React, { useCallback, useMemo, useState } from 'react';
import { ActionIcon, Group, Stack, Table, Text } from '@mantine/core';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { RxDragHandleDots2 } from 'react-icons/rx';
import { HiPlus } from 'react-icons/hi2';

import classes from './SortableFieldsTable.module.css';
import SharedSortableRow from './SharedSortableRow.tsx';
import { dropAnimation } from '@/pages/Editor/components/tree/dndConfig.ts';
import ConfirmationModal from '@/components/ConfirmationModal.tsx';
import { shiftOverlayBy } from '@/pages/Editor/components/StepSidebar/StepForms/ExtractDataStepForm/ExtractFieldsModal/SortableTable/utils.ts';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type ColumnStyles = React.CSSProperties & { width?: number | string };

export type ColumnDef<T> = {
  header: React.ReactNode;
  styles?: ColumnStyles;
  cell: (args: {
    row: T;
    update: (partial: Partial<T>) => void;
    allRows: T[];
    deleteRow: () => void;
  }) => React.ReactNode;
};

export type SortableFieldsTableProps<T> = {
  /** Array of rows */
  rows: T[];
  /** Extract a stable unique id used by dnd-kit */
  getId: (row: T) => UniqueIdentifier;
  /** Called with updated rows (after edits, reorders, deletions, additions) */
  onChange: (next: T[]) => void;
  /** Column configuration */
  columns: ColumnDef<T>[];
  /** Factory for a new blank row */
  makeEmptyRow: () => T;
  /** Short helper text above the table */
  helperText?: React.ReactNode;
  /** Text in the trailing "Add new" row */
  addRowLabel?: string;
  /** Overlay offset for nicer cursor placement (x,y) */
  overlayOffset?: { x: number; y: number };
  /** Optional extra error messages rendered underneath */
  errors?: (React.ReactNode | null | undefined)[];
  /** Delete confirmation props */
  confirm: {
    title: string;
    message: string;
    confirmText?: string;
  };
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

function SortableFieldsTable<T>(props: SortableFieldsTableProps<T>) {
  const {
    rows,
    getId,
    onChange,
    columns,
    makeEmptyRow,
    helperText,
    addRowLabel = 'Add New',
    overlayOffset = { x: 182, y: 44 },
    errors = [],
    confirm,
  } = props;

  // Local state
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  // Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  // Derived
  const items = useMemo(() => rows.map(getId), [rows, getId]);

  const activeRowIndex = useMemo(() => {
    if (!activeId) return -1;
    return rows.findIndex((r) => getId(r) === activeId);
  }, [activeId, rows, getId]);

  // Row-level handlers
  const updateAt = useCallback(
    (partial: Partial<T>, index: number) => {
      const next = [...rows];
      next[index] = { ...next[index], ...partial } as T;
      onChange(next);
    },
    [rows, onChange],
  );

  const add = useCallback(() => {
    onChange([...rows, makeEmptyRow()]);
  }, [rows, makeEmptyRow, onChange]);

  const removeAt = useCallback(
    (index: number) => {
      onChange(rows.slice(0, index).concat(rows.slice(index + 1)));
    },
    [rows, onChange],
  );

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setActiveId(null);
      if (!over || active.id === over.id) return;

      const oldIndex = rows.findIndex((r) => getId(r) === active.id);
      const newIndex = rows.findIndex((r) => getId(r) === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      onChange(arrayMove(rows, oldIndex, newIndex));
    },
    [rows, onChange, getId],
  );

  const renderCells = useCallback(
    (rowIndex: number) =>
      columns.map((column, columnIndex) => (
        <Table.Td key={`${String(column.header)}-${columnIndex}`} style={column.styles}>
          {column.cell({
            row: rows[rowIndex],
            update: (p) => updateAt(p, rowIndex),
            allRows: rows,
            deleteRow: () => setDeleteIndex(rowIndex),
          })}
        </Table.Td>
      )),
    [columns, rows, updateAt],
  );

  return (
    <Stack w="100%" gap={0}>
      {helperText ? (
        <Text my="md" c="dimmed" size="xs">
          {helperText}
        </Text>
      ) : null}

      <DndContext
        sensors={sensors}
        collisionDetection={(args) => {
          const collisions = pointerWithin(args);
          return collisions.length > 0 ? collisions : closestCenter(args);
        }}
        modifiers={[shiftOverlayBy(overlayOffset.x, overlayOffset.y)]}
        onDragStart={(e) => setActiveId(e.active.id)}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <Table
          withColumnBorders
          withRowBorders
          stickyHeader
          highlightOnHover
          maw="100%"
          classNames={{ thead: classes.headerRow, th: classes.headerText, tr: classes.tableRow }}
        >
          <Table.Thead>
            <Table.Tr>
              {/* drag handle / index column */}
              <Table.Th style={{ width: 50 }} />
              {columns.map((column, index) => (
                <Table.Th key={`${String(column.header)}-${index}`} style={column.styles}>
                  {column.header}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
              {rows.map((row, index) => (
                <SharedSortableRow
                  key={String(getId(row))}
                  id={getId(row)}
                  index={index}
                  renderCells={renderCells}
                />
              ))}
            </SortableContext>

            {/* Add new row (not draggable) */}
            <Table.Tr onClick={add} style={{ cursor: 'pointer' }}>
              <Table.Td colSpan={columns.length + 2}>
                <Group c="dimmed" className={classes.addNewField}>
                  <HiPlus />
                  <Text size="xs">{addRowLabel}</Text>
                </Group>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>

        {/* Compact overlay ghost while dragging */}
        <DragOverlay dropAnimation={dropAnimation}>
          {activeId != null && activeRowIndex !== -1 ? (
            <Group
              style={{
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                background: '#f7f7f7',
                borderRadius: 4,
                width: 300,
                height: 50,
                cursor: 'grabbing',
              }}
              wrap="nowrap"
            >
              <Group
                wrap="nowrap"
                style={{ borderRight: '1px solid #dee2e6' }}
                pr={10}
                h="100%"
                w="60px"
                justify="space-between"
              >
                <ActionIcon variant="subtle" color="#868e96" bg="transparent" disabled>
                  <RxDragHandleDots2 />
                </ActionIcon>
                <Text c="dimmed" size="xs">
                  {activeRowIndex + 1}
                </Text>
              </Group>

              {/* Show first column's cell as a quick visual of the row */}
              {columns[0]?.cell({
                row: rows[activeRowIndex],
                update: () => {},
                allRows: rows,
                deleteRow: () => {},
              })}
            </Group>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Error messages */}
      {errors?.map((e, i) => (
        <div key={i}>{e}</div>
      ))}

      <ConfirmationModal
        opened={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        title={confirm.title}
        onConfirm={() => deleteIndex !== null && removeAt(deleteIndex)}
        message={confirm.message}
        confirmType="delete"
        confirmText={confirm.confirmText ?? 'Yes, delete'}
      />
    </Stack>
  );
}

export default SortableFieldsTable;
